<script setup lang="ts">
const localePath = useLocalePath();
const { locale } = useI18n();

// Default values (optional, set in script or template)
const {
  id = undefined,
  buttonTarget = undefined,
  buttonText = "View more",
  buttonVariant = "white",
  imageLazyLoad = true,
  showImagePlaceholder = true,
  headlineLevel = 3,
} = defineProps<{
  image?: string;
  cta?: unknown;
  id?: string;
  badge?: string;
  title?: string;
  headlineLevel?: number;
  pageType?: string;
  text?: string;
  buttonUrl?: string;
  buttonText?: string;
  buttonVariant?: string;
  buttonTarget?: "_blank" | "_self";
  imageLazyLoad?: boolean;
  showImagePlaceholder?: boolean;
  imageSizes?: string;
}>();

const { data: linkedPageData, execute: fetchLinkedPage } = useFetch(
  `/api/v2/marketing/page-by-id/${id}`,
  {
    query: {
      locale,
    },
    immediate: false,
    key: `page-${id}-${locale.value}`,
  },
);

if (id) {
  fetchLinkedPage();
}

const pageType = computed(() => {
  return linkedPageData.value?.pageType || "unknown";
});

const localisedSlug = computed(() => {
  if (pageType.value === "hotel") {
    //Uses the slug that is already resolved and passed in buttonUrl prop
    return;
  }

  if (linkedPageData.value) {
    return localePath({
      name: pageType.value === "blogArticle" ? "blog-slug" : "slug",
      params: {
        slug: linkedPageData.value.slug?.split("/"),
      },
    });
  }
});
</script>

<template>
  <div class="slide contentful-columns-slide">
    <figure v-if="image || showImagePlaceholder" class="cfcs-img img-cover">
      <img
        v-if="typeof image == 'string'"
        :src="image"
        :alt="title"
        :loading="imageLazyLoad ? 'lazy' : 'eager'"
      />

      <ContentfulImage v-else-if="image" v-bind="image" :sizes="imageSizes" />
    </figure>

    <div class="cfcs-body" :class="text ? '' : 'no-text'">
      <Badge
        variant="black"
        v-if="badge"
        class="mb16"
        :class="image ? 'mt24' : ''"
      >
        {{ badge }}
      </Badge>
      <H
        v-if="title"
        :level="headlineLevel"
        size="7"
        class="mb12 cfcs-headline"
        :class="badge ? 'mt0' : image ? 'mt16' : 'mt0'"
        >{{ title }}</H
      >
      <P v-if="text && typeof text === 'string'" class="mt0 mb24">{{ text }}</P>
      <ContentfulAtomRichText
        v-else-if="text"
        :content="text"
        class="mt0 mb24"
      />
    </div>

    <div v-if="buttonUrl?.startsWith('https')">
      <NuxtLink class="arrow-link" :href="buttonUrl">
        {{ buttonText }}
        <Icon class="arrow-link--external-icon" name="citizenm:external-link" />
      </NuxtLink>
    </div>
    <template v-else>
      <ArrowLink
        class="ctf-arrow-link"
        v-if="
          !localisedSlug && buttonUrl && buttonText && buttonVariant != 'arrow'
        "
        :href="buttonUrl"
        :target="buttonTarget"
      >
        {{ buttonText }}
      </ArrowLink>

      <ArrowLink
        class="ctf-arrow-link"
        v-if="localisedSlug"
        :href="localisedSlug"
        :target="buttonTarget"
      >
        {{ buttonText }}
      </ArrowLink>
      <ArrowLink
        class="ctf-arrow-link"
        :href="buttonUrl"
        v-if="buttonUrl && buttonText && buttonVariant === 'arrow'"
        :target="buttonTarget"
      >
        <span>{{ buttonText }}</span>
      </ArrowLink>
    </template>
  </div>
</template>

<style lang="scss">
.contentful-columns-slide {
  p {
    color: var(--secondary-black);
  }
}

.agile__slide.contentful-columns-slide {
  display: flex;
  flex-direction: column;
  height: auto !important;

  // margin-right: var(--space-24);
  padding: 0 var(--space-12);

  &:last-child {
    margin-right: 0;
  }

  .arrow-link {
    font-family: var(--font-family-bold);

    svg {
      top: 0;
    }

    &--external-icon {
      margin-inline-start: var(--cm-spacing-3, 0.75rem);
    }
  }

  @include mdx {
    padding: 0 var(--space-6);
  }
}

.cfcs-img {
  @extend %image-cover;

  border-radius: 4px;

  &::after {
    padding-bottom: 75% !important;
  }
}

.cfcs-body {
  flex-grow: 1;

  &.no-text {
    margin-bottom: var(--space-24);
  }
}

.cfcs-btn {
  align-self: flex-start;
}

.cfcs-headline {
  @include mdx {
    font-size: var(--font-size-h7) !important;
    line-height: var(--line-height-h7) !important;
  }
}
</style>
